import {
    type Card,
    CardStatusEnum,
    type CostCenter,
    type Employee,
    type Expense,
    type ExportHistory,
    type Transaction,
    type TransactionCategory,
    type TripFolder,
    type Vendor,
    type Workflow,
} from "@finway-group/shared/lib/models"
import { type Reducer } from "redux"
import { type InboxDocument } from "~Shared/models/inboxDocument"

import { type ReactQueryTableData } from "~Shared/queries/consts"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { getTableDefaultSort } from "~Shared/utils/table.utils"
import type {
    FetchTableFailureAction,
    FetchTableSuccessAction,
    InitialFetchTablesAction,
    RefetchCardRelatedTablesAction,
    RefetchExpenseRelatedTablesAction,
    RefetchInboxInvoiceTablesAction,
    RefetchMultipleTablesAction,
    RefetchTableAction,
    RefetchTransactionCategoryRelatedTables,
    RefetchUserRelatedTablesAction,
    TableActions,
    TableFetchingAction,
    UpdateDashboardTablesFilterAction,
    UpdateDashboardTablesSearchAction,
    UpdateTableFilterAction,
    UpdateTablePaginationAction,
    UpdateTableSearchAction,
    UpdateTableSortAction,
} from "../actions/tables/tableTypes"
import {
    FetchTableFailureTypes,
    FetchTableSuccessTypes,
    RefetchTableTypes,
    TableActionsTypes,
    TableFetchingTypes,
    UpdateTableFilterType,
    UpdateTablePaginationType,
    UpdateTableSearchType,
    UpdateTableSortType,
} from "../actions/tables/tableTypes.enum"

export interface TableData<T> {
    docs: Array<T>
    totalDocs: number
    limit: number
    totalPages: number
    page: number // the actual page the table is in based on the current filter/search
    sort: {
        field: string
        order: string // ascend / descend
    }
    filter: { [key: string]: string | object | Array<string> }
    search: string
}

export interface Table<T> {
    data: TableData<T>
    isFetching: boolean
    error: any
    /**
     * Monotonically increasing id, bumped every time a refetch is requested. Paired with startedFetchRequestId
     * it replaces the plain boolean flag this used to be: because the id only ever grows, a fetch that resolves
     * late can no longer swallow a refetch that was requested while it was still in flight, and a request can
     * never collapse to "nothing to do" before React has observed it.
     */
    fetchRequestId: number
    /** The fetchRequestId that the most recently started fetch was launched for. */
    startedFetchRequestId: number
    /** The fetchRequestId of the fetch that turned isFetching on; a dropped queued request never changes it. */
    loadingFetchRequestId?: number
}

/** True while a requested refetch has not been picked up by a fetch yet. */
export const isFetchPending = (table: Pick<Table<unknown>, "fetchRequestId" | "startedFetchRequestId">) => table.fetchRequestId > table.startedFetchRequestId

/**
 * True while a refetch is queued or a fetch is in flight. A finishing fetch no longer empties
 * the queue on its own, so search/filter loading must stay on until this is false.
 */
export const isTableFetchInProgress = (table: Pick<Table<unknown>, "fetchRequestId" | "startedFetchRequestId" | "isFetching">): boolean => isFetchPending(table) || table.isFetching

/**
 * True when a finishing fetch belongs to a request that is no longer the latest one queued.
 * Cancel cannot be relied on here: the axios interceptor overwrites per-request cancel tokens,
 * and a response that already resolved still dispatches success/failure.
 */
export const isStaleTableFetchResult = (table: Pick<Table<unknown>, "fetchRequestId">, fetchRequestId: number): boolean => fetchRequestId !== table.fetchRequestId

export type EmployeeWithExpenseData = Employee & { expenseData?: { amount: number; value: number; id: string } }
export type VendorWithExpenseData = Vendor & { expenseData?: { amount: number; value: number; id: string } }
export type CostCenterWithBudgetData = CostCenter & { budgetData?: { capacity: number; consumption: number; forecast: number; id: string } }
export type TransactionCategoryWithChildren = Omit<TransactionCategory, "children"> & { children: Array<TransactionCategory> }

export interface TablesState {
    inboxInvoiceTable: Table<InboxDocument>
    todoPurchaseApprovalTable: Table<Expense>
    todoInvoiceApprovalTable: Table<Expense>
    todoInvoiceAndPurchaseApprovalTable: Table<Expense>
    todoDocsNeededTable: Table<Expense>
    todoApprovalPendingTable: Table<Expense>
    reviewTable: Table<Expense>
    payAndExportToBePaidTable: Table<Expense>
    payAndExportToBeExportedTable: Table<Expense>
    oposListTable: Table<Expense>
    oposExcludedListTable: Table<Expense>
    doneTable: Table<Expense>
    allRequestsTable: Table<Expense>
    inProgressTable: Table<Expense>
    subscriptionTable: Table<Expense>
    possibleMatchesTable: Table<Expense>
    cardTransactionsTable: Table<Transaction>
    transactionInflowTable: Table<Transaction>
    transactionOutflowTable: Table<Transaction>
    transactionAllTable: Table<Transaction>
    cardTable: Table<Card>
    costcenterTable: Table<CostCenterWithBudgetData>
    vendorTable: Table<VendorWithExpenseData>
    workflowTable: Table<Workflow>
    inboxInvoiceModalExpensesTable: Table<Expense>
    inboxInvoiceModalInvoicesTable: Table<InboxDocument>
    transactionCategoryInflowTable: Table<TransactionCategoryWithChildren>
    transactionCategoryOutflowTable: Table<TransactionCategoryWithChildren>
    archiveTable: Table<Expense>
    expenseTransactionsTable: Table<Transaction>
    transactionArchiveTable: Table<Transaction>
    attachExpenseTable: Table<Expense>
    draftTripFolderTable: Table<TripFolder>
    exportHistoryTable: Table<ExportHistory>
}

export const createInitialReactQueryTableData = (limit: number = 20): ReactQueryTableData<any> => ({
    docs: [],
    totalDocs: 0,
    limit,
    page: 1,
    totalPages: 0,
    pagingCounter: 1,
    hasPrevPage: false,
    hasNextPage: false,
    prevPage: null,
    nextPage: null,
})

export const createInitialTable = (
    { filter, sort }: { filter?: { [key: string]: string | object | Array<string> }; sort?: { field: string; order: string } } = {},
    limit: number = 20,
) => ({
    data: {
        docs: [],
        totalDocs: 0,
        totalPages: 0,

        // query data
        page: 1,
        limit,
        sort: sort || { field: "", order: "" },
        filter: filter || {},
        search: "",
    },
    isFetching: false,
    error: undefined,
    fetchRequestId: 0,
    startedFetchRequestId: 0,
})

const createInitialTablesState = () => ({
    inboxInvoiceTable: createInitialTable({ sort: getTableDefaultSort(TablesEnum.INBOX_INVOICE) }),
    todoPurchaseApprovalTable: createInitialTable(),
    todoInvoiceApprovalTable: createInitialTable(),
    todoDocsNeededTable: createInitialTable(),
    todoInvoiceAndPurchaseApprovalTable: createInitialTable(),
    todoApprovalPendingTable: createInitialTable(),
    reviewTable: createInitialTable(),
    payAndExportToBePaidTable: createInitialTable(),
    payAndExportToBeExportedTable: createInitialTable(),
    oposListTable: createInitialTable({ sort: getTableDefaultSort(TablesEnum.OPOS_LIST) }),
    oposExcludedListTable: createInitialTable({ sort: getTableDefaultSort(TablesEnum.OPOS_EXCLUDED_LIST) }),
    doneTable: createInitialTable({ sort: getTableDefaultSort(TablesEnum.DONE_REQUESTS) }),
    allRequestsTable: createInitialTable(),
    inProgressTable: createInitialTable({ sort: getTableDefaultSort(TablesEnum.IN_PROGRESS) }),
    subscriptionTable: createInitialTable({ filter: { isPaused: "false" }, sort: getTableDefaultSort(TablesEnum.SUBSCRIPTIONS) }),
    possibleMatchesTable: createInitialTable(),
    cardTransactionsTable: createInitialTable(),
    transactionInflowTable: createInitialTable({ sort: getTableDefaultSort(TablesEnum.TRANSACTIONS_INFLOW) }),
    transactionOutflowTable: createInitialTable({ sort: getTableDefaultSort(TablesEnum.TRANSACTIONS_OUTFLOW) }),
    transactionAllTable: createInitialTable({ sort: getTableDefaultSort(TablesEnum.TRANSACTIONS_ALL) }),
    cardTable: createInitialTable({ filter: { status: CardStatusEnum.ACTIVE }, sort: getTableDefaultSort(TablesEnum.CARDS) }),
    costcenterTable: createInitialTable(),
    vendorTable: createInitialTable(),
    workflowTable: createInitialTable(),
    inboxInvoiceModalExpensesTable: createInitialTable(),
    inboxInvoiceModalInvoicesTable: createInitialTable(),
    archiveTable: createInitialTable(),
    transactionCategoryInflowTable: createInitialTable(),
    transactionCategoryOutflowTable: createInitialTable(),
    expenseTransactionsTable: createInitialTable({}, 5),
    draftTripFolderTable: createInitialTable({}, 5),
    attachExpenseTable: createInitialTable(),
    transactionArchiveTable: createInitialTable({ sort: getTableDefaultSort(TablesEnum.TRANSACTIONS_ARCHIVE) }),
    exportHistoryTable: createInitialTable(),
})

export const initialTablesState = createInitialTablesState()

/**
 * Applies the boolean "shouldFetch" of an action payload to the request counters: `true` queues a refetch,
 * `false` drops a queued one (used by the UI actions that change table state without wanting a refetch).
 */
const applyFetchRequest = <T extends Pick<Table<unknown>, "fetchRequestId" | "startedFetchRequestId">>(table: T, shouldFetch: boolean): T =>
    shouldFetch ? { ...table, fetchRequestId: table.fetchRequestId + 1 } : { ...table, startedFetchRequestId: table.fetchRequestId }

/**
 * Rows are only kept here for the tables that redux still fetches. A table whose rows moved to react query keeps
 * its table config but has no slice left, so an action naming it has nothing to write and must leave state alone.
 */
const getTableSlice = (state: TablesState, table: TablesEnum): Table<unknown> | undefined => (state as Partial<Record<TablesEnum, Table<unknown>>>)[table]

const setTableFetching = (state: TablesState, action: TableFetchingAction): TablesState => {
    const { table, isFetching, fetchRequestId } = action.payload
    const tableSlice = getTableSlice(state, table)
    if (!tableSlice) {
        return state
    }
    if (!isFetching) {
        // a fetch that ended without a result only stops the loading it started itself
        return tableSlice.loadingFetchRequestId === fetchRequestId ? { ...state, [table]: { ...tableSlice, isFetching: false } } : state
    }
    // Never move the marker backwards: a fetch started for an older request must not un-queue a newer one.
    const startedFetchRequestId = Math.max(tableSlice.startedFetchRequestId, fetchRequestId)
    return { ...state, [table]: { ...tableSlice, isFetching, startedFetchRequestId, loadingFetchRequestId: fetchRequestId } }
}

const shouldIgnoreFetchResult = (tableState: Table<unknown>, fetchRequestId: number | undefined): boolean =>
    // Untagged results (e.g. a superseded cost-center budget enrichment) have no generation token, so they
    // must not be treated as current: they would overwrite fresher rows and clear an in-flight loading flag.
    fetchRequestId === undefined || isStaleTableFetchResult(tableState, fetchRequestId)

// A finishing fetch deliberately leaves the request counters alone, so a refetch requested while it was in
// flight stays queued instead of being cleared by a response that predates it.
const setTableData = <T>(state: TablesState, action: FetchTableSuccessAction<T>): TablesState => {
    const { table, isFetching, error, fetchRequestId } = action.payload
    const tableSlice = getTableSlice(state, table)
    if (!tableSlice || shouldIgnoreFetchResult(tableSlice, fetchRequestId)) {
        return state
    }
    const data = { ...tableSlice.data, ...action.payload.data, count: action.payload.data.totalDocs }
    return { ...state, [table]: { ...tableSlice, data, isFetching, error } }
}

const resetExpenseArchiveTable = (state: TablesState): TablesState => ({ ...state, archiveTable: createInitialTable() })

const setTableError = (state: TablesState, action: FetchTableFailureAction): TablesState => {
    const { table, error, isFetching, fetchRequestId } = action.payload
    const tableSlice = getTableSlice(state, table)
    if (!tableSlice || shouldIgnoreFetchResult(tableSlice, fetchRequestId)) {
        return state
    }
    return { ...state, [table]: { ...tableSlice, error, isFetching } }
}

const setTableShouldFetch = (state: TablesState, action: RefetchTableAction): TablesState => {
    const { table, shouldFetch } = action.payload
    const tableSlice = getTableSlice(state, table)
    if (!tableSlice) {
        return state
    }
    return { ...state, [table]: applyFetchRequest(tableSlice, shouldFetch) }
}

// Queues a fetch on all tables so they are fetched the first time the user navigates to the component where the table is.
const initialFetchTables = (state: TablesState, action: InitialFetchTablesAction): TablesState => {
    const stateCopy: any = { ...state }
    for (const table of Object.keys(stateCopy)) {
        if (table === TablesEnum.ARCHIVE) {
            continue
        } // The archive table should not be initially fetch, but rather when the user enters a fetching period
        stateCopy[table] = applyFetchRequest(stateCopy[table], action.payload.shouldFetch)
    }
    return stateCopy
}

const resetAllTables = (): TablesState => createInitialTablesState()

const refetchInboxInvoiceTables = (state: TablesState, action: RefetchInboxInvoiceTablesAction): TablesState => {
    const { shouldFetch } = action.payload
    return {
        ...state,
        inboxInvoiceTable: applyFetchRequest(state.inboxInvoiceTable, shouldFetch),
        inboxInvoiceModalInvoicesTable: applyFetchRequest(state.inboxInvoiceModalInvoicesTable, shouldFetch),
    }
}

const refetchRelatedTables = (
    state: TablesState,
    action:
        | RefetchMultipleTablesAction
        | RefetchCardRelatedTablesAction
        | RefetchUserRelatedTablesAction
        | RefetchExpenseRelatedTablesAction
        | RefetchTransactionCategoryRelatedTables,
): TablesState => {
    const { shouldFetch, tables } = action.payload
    const stateCopy: any = { ...state }
    for (const table of tables) {
        const tableSlice = getTableSlice(state, table)
        if (tableSlice) {
            stateCopy[table] = applyFetchRequest(tableSlice, shouldFetch)
        }
    }
    return stateCopy
}

// Every dashboard tab shares the same search box and filter bar, so they are updated together.
const DASHBOARD_TABLES = [
    TablesEnum.TODO_PURCHASE_APPROVAL,
    TablesEnum.TODO_INVOICE_APPROVAL,
    "todoInvoiceAndPurchaseApprovalTable", // has no TablesEnum member, it only exists in the store

    TablesEnum.TODO_DOCS_NEEDED,
    TablesEnum.TODO_APPROVAL_PENDING,
    TablesEnum.PAY_AND_EXPORT_TO_BE_PAID,
    TablesEnum.PAY_AND_EXPORT_TO_BE_EXPORTED,
    TablesEnum.REVIEW_REQUESTS,
    TablesEnum.DONE_REQUESTS,
    TablesEnum.IN_PROGRESS,
    TablesEnum.ALL_REQUESTS,
    TablesEnum.TRIP_FOLDER_DRAFT,
] as const

const updateDashboardTables = (state: TablesState, shouldFetch: boolean, dataPatch: Partial<TableData<never>>): TablesState => {
    const stateCopy: any = { ...state }
    for (const table of DASHBOARD_TABLES) {
        const updatedTable = applyFetchRequest(stateCopy[table], shouldFetch)
        stateCopy[table] = { ...updatedTable, data: { ...updatedTable.data, ...dataPatch } }
    }
    return stateCopy
}

const updateDashboardTablesSearch = (state: TablesState, action: UpdateDashboardTablesSearchAction): TablesState => {
    const { shouldFetch, search, page } = action.payload
    return updateDashboardTables(state, shouldFetch, { search, page })
}

const updateDashboardTablesFilter = (state: TablesState, action: UpdateDashboardTablesFilterAction): TablesState => {
    const { shouldFetch, filter, page } = action.payload
    return updateDashboardTables(state, shouldFetch, { filter, page })
}

const updateTablePagination = (state: TablesState, action: UpdateTablePaginationAction): TablesState => {
    const { table, page, limit, shouldFetch } = action.payload
    const tableSlice = getTableSlice(state, table)
    if (!tableSlice) {
        return state
    }
    const updatedTable = applyFetchRequest(tableSlice, shouldFetch)
    return { ...state, [table]: { ...updatedTable, data: { ...updatedTable.data, page, limit } } }
}

const setTableSort = (state: TablesState, action: UpdateTableSortAction): TablesState => {
    const { table, sort, shouldFetch } = action.payload
    const tableSlice = getTableSlice(state, table)
    if (!tableSlice) {
        return state
    }
    const updatedTable = applyFetchRequest(tableSlice, shouldFetch)
    return { ...state, [table]: { ...updatedTable, data: { ...updatedTable.data, sort: { ...updatedTable.data.sort, order: sort.order, field: sort.field } } } }
}

const setTableSearch = (state: TablesState, action: UpdateTableSearchAction): TablesState => {
    const { table, search, page, shouldFetch } = action.payload
    const tableSlice = getTableSlice(state, table)
    if (!tableSlice) {
        return state
    }
    const updatedTable = applyFetchRequest(tableSlice, shouldFetch)
    return { ...state, [table]: { ...updatedTable, data: { ...updatedTable.data, search, page } } }
}

const setTableFilter = (state: TablesState, action: UpdateTableFilterAction): TablesState => {
    const { table, filter, page, shouldFetch } = action.payload
    const tableSlice = getTableSlice(state, table)
    if (!tableSlice) {
        return state
    }
    const updatedTable = applyFetchRequest(tableSlice, shouldFetch)
    return { ...state, [table]: { ...updatedTable, data: { ...updatedTable.data, filter, page } } }
}

export const tableReducer: Reducer<TablesState, TableActions> = (state = initialTablesState, action) => {
    switch (action.type) {
        // All tables
        case TableActionsTypes.INITIAL_FETCH_TABLES:
            return initialFetchTables(state, action)
        case TableActionsTypes.RESET_ALL_TABLES:
            return resetAllTables()

        // Dashboard tables
        case TableActionsTypes.UPDATE_DASHBOARD_TABLES_FILTER:
            return updateDashboardTablesFilter(state, action)
        case TableActionsTypes.UPDATE_DASHBOARD_TABLES_SEARCH:
            return updateDashboardTablesSearch(state, action)
        case TableActionsTypes.RESET_TABLE_ARCHIVE:
            return resetExpenseArchiveTable(state)

        // Inbox invoice tables
        case TableActionsTypes.REFETCH_INBOX_INVOICE_TABLES:
            return refetchInboxInvoiceTables(state, action)

        case TableActionsTypes.REFETCH_EXPENSE_RELATED_TABLES:
        case TableActionsTypes.REFETCH_CARD_RELATED_TABLES:
        case TableActionsTypes.REFETCH_USER_RELATED_TABLES:
        case TableActionsTypes.REFETCH_TRANSACTION_CATEGORY_RELATED_TABLES:
        case TableActionsTypes.REFETCH_MULTIPLE_TABLES:
            return refetchRelatedTables(state, action)

        default:
            return singleTableReducer(state, action)
    }
}

const singleTableReducer: Reducer<TablesState, TableActions> = (state = initialTablesState, action) => {
    switch (true) {
        case Object.values(TableFetchingTypes).includes(action.type as TableFetchingTypes):
            return setTableFetching(state, action as TableFetchingAction)

        case Object.values(FetchTableSuccessTypes).includes(action.type as any):
            return setTableData(state, action as FetchTableSuccessAction)

        case Object.values(FetchTableFailureTypes).includes(action.type as any):
            return setTableError(state, action as FetchTableFailureAction)

        case Object.values(RefetchTableTypes).includes(action.type as RefetchTableTypes):
            return setTableShouldFetch(state, action as RefetchTableAction)

        case Object.values(UpdateTablePaginationType).includes(action.type as UpdateTablePaginationType):
            return updateTablePagination(state, action as UpdateTablePaginationAction)

        case Object.values(UpdateTableSortType).includes(action.type as UpdateTableSortType):
            return setTableSort(state, action as UpdateTableSortAction)

        case Object.values(UpdateTableSearchType).includes(action.type as UpdateTableSearchType):
            return setTableSearch(state, action as UpdateTableSearchAction)

        case Object.values(UpdateTableFilterType).includes(action.type as UpdateTableFilterType):
            return setTableFilter(state, action as UpdateTableFilterAction)

        default:
            return state
    }
}
